import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { RRule } from 'rrule';
import { View } from '@shared/components/View';
import { MBTextBody1 } from '@shared/components/typography';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import { isDateComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { useBoolean } from '@shared/util/hooks';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { EditFieldContainer } from '../EditFieldContainer';
import { DateEditFieldPicker } from './DateEditFieldPicker';
import { RepeatText } from './RepeatText';
import { useBatchRule } from './hooks/useBatchRule';
import { useEntityBatch } from './hooks/useEntityBatch';
import { useIsNeverEnabled } from './hooks/useIsNeverEnabled';
import { DateProps, RepeatEndType } from './types';
import { buildCustomRRule } from './utils';

export function DateEditField({
  field,
  component,
  updateField,
  workflowTemplateId,
  entityBatchField,
  error,
  includeWrapperPadding = true,
  allowClear = true,
  showIcon,
  label,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: DateProps) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const { data: entityBatch, loading: entityBatchLoading } = useEntityBatch(
    entityBatchField?.id,
    workflowTemplateId,
  );
  const anchorName = `--date-edit-${component.id}`;
  const onClear = useCallback(() => {
    updateField?.(component.id, null);
  }, [component.id, updateField]);
  const {
    rruleOptions,
    setRruleOptions,
    canBatchCreate,
    userIsEditing,
    setUserIsEditing,
    minimumInSeries,
    tagFilter,
    setMinimumInSeries,
    setTagFilter,
    recurrenceType,
    offset,
    setOffset,
  } = useBatch();
  const isReadOnly = component.readonly || isNil(updateField);

  // Validate offset configuration
  const hasValidTagFilter =
    !isNil(tagFilter?.componentId) && !isEmpty(tagFilter?.value?.containsString?.values);
  const hasValidOffset = !isNil(offset?.unit) && !isNil(offset?.value) && offset.value > 0;
  const isValidOneAtATime = hasValidTagFilter && hasValidOffset;

  const showRepeatText =
    isDateComponent(component) &&
    component.allowEntityBatchCreation &&
    (!isNil(rruleOptions) || isValidOneAtATime);
  const [isDatePickerOpen, showDatePicker, hideDatePicker] = useBoolean(false);
  const isBatchRuleEnabled =
    isDateComponent(component) && canBatchCreate && !!component.allowEntityBatchCreation;

  // If we're loading an existing entity batch, wait for it to load before initializing batch rule
  const isWaitingForEntityBatch = !isNil(entityBatchField?.id) && entityBatchLoading;

  const {
    repeatPresets,
    filteredRepeatPresets,
    repeatCount,
    setRepeatCount,
    presetIndexSelected,
    setPresetIndexSelected,
    isCustomRepeatSelected,
    isOffsetSelected,
    repeatUntil,
    setRepeatUntil,
    repeatEndType,
    setRepeatEndType,
    resetBatchOptions,
  } = useBatchRule({
    rruleOptions,
    setRruleOptions,
    recurrenceType,
    field,
    isBatchRuleEnabled,
    userIsEditing,
    component: isDateComponent(component) ? component : undefined,
    setMinimumInSeries,
    setTagFilter,
    isWaitingForEntityBatch,
  });

  // Determine if the "Never" end-type option is currently valid
  const isNeverEndTypeEnabled = useIsNeverEnabled({ minimumInSeries, tagFilter });

  // revert to end date when date picker is closed, if never is not valid
  const prevDatePickerOpenRef = useRef<boolean>(false);

  useEffect(() => {
    // Skip if batch rule is not enabled
    if (!isBatchRuleEnabled) {
      prevDatePickerOpenRef.current = isDatePickerOpen;
      return;
    }
    const closed = prevDatePickerOpenRef.current && !isDatePickerOpen;

    if (closed && repeatEndType === RepeatEndType.NEVER && !isNeverEndTypeEnabled) {
      setRepeatEndType(RepeatEndType.END_DATE);
    }

    prevDatePickerOpenRef.current = isDatePickerOpen;
  }, [
    isDatePickerOpen,
    repeatEndType,
    isNeverEndTypeEnabled,
    setRepeatEndType,
    isBatchRuleEnabled,
  ]);

  // set rrule options when entity batch is set
  // Use a ref to track the last rrule string we've seen to avoid re-parsing on every render
  const lastRruleStringRef = useRef<string | null>(null);
  useEffect(() => {
    // Only sync rrule options when entity batch creation is enabled for this component
    if (!isDateComponent(component) || !component.allowEntityBatchCreation) {
      return;
    }
    if (isNil(entityBatch)) {
      return;
    }
    const rrule = entityBatch.getRecurringEntitiesSchedule.rrule;
    if (isNil(rrule)) {
      return;
    }
    // Skip if we've already processed this exact rrule string
    if (lastRruleStringRef.current === rrule) {
      return;
    }
    lastRruleStringRef.current = rrule;
    setRruleOptions(RRule.fromString(rrule).options);
  }, [entityBatch, setRruleOptions, component]);

  // keep the bymonthday in sync with the start date when yearly
  useEffect(() => {
    // Skip if batch rule is not enabled - these sync operations are only needed for batch creation
    if (!isBatchRuleEnabled) {
      return;
    }
    if (isNil(field?.date)) {
      return;
    }

    const startDateDay = new Date(field.date).getDate();
    const startMonth = new Date(field.date).getMonth() + 1;

    if (recurrenceType === 'RRULE_SCHEDULE') {
      setRruleOptions(prev => {
        if (prev?.freq !== RRule.YEARLY) {
          return prev;
        } else {
          const prevByMonthDay = prev?.bymonthday;
          const prevByMonth = prev?.bymonth;
          const hasSameDay = Array.isArray(prevByMonthDay)
            ? prevByMonthDay.length === 1 && prevByMonthDay[0] === startDateDay
            : prevByMonthDay === startDateDay;
          const hasSameMonth = Array.isArray(prevByMonth)
            ? prevByMonth.length === 1 && prevByMonth[0] === startMonth
            : prevByMonth === startMonth;
          if (hasSameDay && hasSameMonth) {
            return prev;
          }

          return buildCustomRRule(
            prev,
            { bymonthday: [startDateDay], bymonth: [startMonth] },
            true,
          );
        }
      });
    } else if (recurrenceType === 'OFFSET') {
      setOffset(prev => {
        const newUnit = prev?.unit ?? 'DAY';
        const newValue = prev?.value ?? 1;
        // Only return a new object if values actually changed
        if (
          prev?.unit === newUnit &&
          prev?.value === newValue &&
          prev?.initialDate === field.date
        ) {
          return prev;
        }
        return {
          unit: newUnit,
          value: newValue,
          initialDate: field.date,
        };
      });
    }
  }, [field?.date, setRruleOptions, setOffset, recurrenceType, isBatchRuleEnabled]);

  const handleClear = () => {
    onClear();
    if (isBatchRuleEnabled) {
      resetBatchOptions();
    }
  };

  const hasValue = !!field?.formattedDate;
  const showClearButton = hasValue && !isReadOnly && allowClear;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      onPress={!isReadOnly ? showDatePicker : undefined}
      showClearButton={showClearButton}
      onClear={handleClear}
      showIcon={showIcon}
      label={label}
      showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasValue}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      error={error}
      includeWrapperPadding={includeWrapperPadding}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      footer={
        showRepeatText && responsiveForms ? (
          <div style={{ paddingTop: grid(0.5), paddingBottom: grid(0.5) }}>
            <RepeatText
              rruleOptions={rruleOptions}
              includeStartDate
              offset={offset}
              tagFilter={tagFilter}
              workflowTemplateId={workflowTemplateId}
            />
          </div>
        ) : undefined
      }
      style={style}
    >
      {hasValue && <MBTextBody1>{field.formattedDate}</MBTextBody1>}
      {!hasValue && (
        <View paddingVertical={1.25}>
          <MBTextBody1 color={colors.foregroundInactive}>{component.placeholder}</MBTextBody1>
        </View>
      )}
      {showRepeatText && !responsiveForms && (
        <View paddingTop={0.5}>
          <RepeatText
            rruleOptions={rruleOptions}
            includeStartDate
            offset={offset}
            tagFilter={tagFilter}
            workflowTemplateId={workflowTemplateId}
          />
        </View>
      )}
      {isDateComponent(component) && isDatePickerOpen && (
        <DateEditFieldPicker
          field={field}
          component={component}
          updateField={updateField}
          onClose={hideDatePicker}
          anchorName={anchorName}
          repeatPresets={repeatPresets}
          filteredRepeatPresets={filteredRepeatPresets}
          presetIndexSelected={presetIndexSelected}
          setPresetIndexSelected={setPresetIndexSelected}
          repeatCount={repeatCount}
          setRepeatCount={setRepeatCount}
          repeatUntil={repeatUntil}
          setRepeatUntil={setRepeatUntil}
          repeatEndType={repeatEndType}
          setRepeatEndType={setRepeatEndType}
          isCustomRepeatSelected={isCustomRepeatSelected}
          isOffsetSelected={isOffsetSelected}
          resetBatchOptions={resetBatchOptions}
          setUserIsEditing={setUserIsEditing}
          workflowTemplateId={workflowTemplateId}
        />
      )}
    </EditFieldContainer>
  );
}
